import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Sparkles, 
  ChevronLeft, 
  ChevronRight, 
  Check,
  Clock,
  Car,
  User,
  CalendarIcon,
  MapPin,
  Droplets,
  Gift
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { formatTimeDisplay, isValidOperatingDay } from "@/utils/dateUtils";
import { 
  validateServiceStep, 
  validateDateTimeStep, 
  validateVehicleStep, 
  validateClientStep 
} from "@/utils/bookingValidation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, name: "Service", icon: Sparkles },
  { id: 2, name: "Date & Time", icon: CalendarIcon },
  { id: 3, name: "Vehicle", icon: Car },
  { id: 4, name: "Details", icon: User },
  { id: 5, name: "Confirm", icon: Check },
];

const DROP_OFF_ADDRESS = "142 Haddon Ave North, Hamilton";

export default function BookingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [services, setServices] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);

  // Form state
  const [selectedService, setSelectedService] = useState(searchParams.get('service') || '');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [vehicle, setVehicle] = useState({ make: '', model: '', license_plate: '', color: '' });
  const [client, setClient] = useState({ name: '', email: '', phone: '', address: '' });
  const [location, setLocation] = useState({ 
    has_driveway: true, 
    has_water_access: true, 
    bring_hose: false, 
    drop_off_location: false 
  });
  const [specialRequests, setSpecialRequests] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [discountInfo, setDiscountInfo] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);

  useEffect(() => {
    const fetchServicesData = async () => {
      try {
        const res = await axios.get(`${API}/services`);
        setServices(res.data);
      } catch (err) {
        toast.error("Failed to load services");
      }
    };
    const fetchBlockedDates = async () => {
      try {
        const res = await axios.get(`${API}/blocked-dates`);
        setBlockedDates(res.data.blocked_dates || []);
      } catch (err) {
        console.error("Failed to load blocked dates");
      }
    };
    fetchServicesData();
    fetchBlockedDates();
  }, []);

  useEffect(() => {
    const fetchSlotsData = async () => {
      if (!selectedDate || !selectedService) return;
      setLoading(true);
      try {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const res = await axios.get(`${API}/available-slots?date=${dateStr}&service_id=${selectedService}`);
        setAvailableSlots(res.data.available_slots);
        if (res.data.message) {
          toast.info(res.data.message);
        }
      } catch (err) {
        toast.error("Failed to load available slots");
        setAvailableSlots([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSlotsData();
  }, [selectedDate, selectedService]);

  const getSelectedServiceData = () => {
    return services.find(s => s.id === selectedService);
  };

  const validateDiscountCode = async () => {
    if (!discountCode.trim() || !selectedService) return;
    
    setValidatingDiscount(true);
    try {
      const res = await axios.get(`${API}/validate-discount?code=${discountCode}&service_id=${selectedService}`);
      if (res.data.valid) {
        setDiscountInfo(res.data);
        toast.success(`Discount applied: ${res.data.percent}% off!`);
      } else {
        setDiscountInfo(null);
        toast.error("Invalid discount code");
      }
    } catch (err) {
      setDiscountInfo(null);
      toast.error("Failed to validate discount code");
    } finally {
      setValidatingDiscount(false);
    }
  };

  const disabledDays = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Check if date is in the past
    if (date < today) return true;
    
    // Check if date is blocked
    const dateStr = format(date, 'yyyy-MM-dd');
    if (blockedDates.includes(dateStr)) return true;
    
    return !isValidOperatingDay(date);
  };

  const handleNext = () => {
    let error = null;
    
    switch (currentStep) {
      case 1:
        error = validateServiceStep(selectedService);
        break;
      case 2:
        error = validateDateTimeStep(selectedDate, selectedTime);
        break;
      case 3:
        error = validateVehicleStep(vehicle);
        break;
      case 4:
        error = validateClientStep(client, location);
        break;
      default:
        break;
    }
    
    if (error) {
      toast.error(error);
      return;
    }
    setCurrentStep(prev => Math.min(prev + 1, 5));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const bookingData = {
        service_id: selectedService,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time_slot: selectedTime,
        client: {
          ...client,
          address: location.drop_off_location ? DROP_OFF_ADDRESS : client.address
        },
        vehicle: vehicle,
        location: location,
        special_requests: specialRequests || null,
        referral_code: referralCode || null,
        discount_code: discountInfo?.valid ? discountCode : null
      };

      const res = await axios.post(`${API}/bookings`, bookingData);
      toast.success("Booking confirmed!");
      navigate(`/confirmation/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create booking");
    } finally {
      setSubmitting(false);
    }
  };

  // Use imported formatTimeDisplay from utils
  const formatTime = formatTimeDisplay;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-slate-900 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-sky-400" />
              <span className="logo-text text-lg text-white">RISE & SHINE</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-gray-400 hover:text-white">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-slate-900 pb-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {STEPS.map((step, idx) => (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div 
                    className={`step-indicator w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all
                      ${currentStep === step.id ? 'active border-sky-500' : 
                        currentStep > step.id ? 'completed border-sky-500' : 'border-gray-600 text-gray-600'}`}
                  >
                    {currentStep > step.id ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <step.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={`text-xs mt-2 hidden sm:block ${
                    currentStep >= step.id ? 'text-white' : 'text-gray-600'
                  }`}>
                    {step.name}
                  </span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`w-12 sm:w-20 h-0.5 mx-2 ${
                    currentStep > step.id ? 'bg-sky-500' : 'bg-gray-600'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="booking-page">
        {/* Step 1: Select Service */}
        {currentStep === 1 && (
          <div data-testid="step-service">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground uppercase tracking-tight mb-6">
              Choose Your Service
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {services.map((service) => (
                <Card 
                  key={service.id}
                  className={`cursor-pointer transition-all hover:shadow-lg ${
                    selectedService === service.id 
                      ? 'ring-2 ring-sky-500 bg-sky-500/5' 
                      : 'bg-card hover:bg-card/80'
                  } ${service.id === 'deal' ? 'relative' : ''}`}
                  onClick={() => setSelectedService(service.id)}
                  data-testid={`select-service-${service.id}`}
                >
                  {service.id === 'deal' && (
                    <div className="absolute -top-3 left-4 bg-sky-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      BEST VALUE
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-foreground mb-1">{service.name}</h3>
                        <p className="text-sm text-muted-foreground mb-3">{service.description}</p>
                        <div className="flex items-center gap-4">
                          <span className="text-2xl font-bold text-sky-500">${service.price}</span>
                          <span className="text-sm text-muted-foreground flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {service.duration_hours} hr{service.duration_hours > 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        selectedService === service.id 
                          ? 'border-sky-500 bg-sky-500' 
                          : 'border-gray-300'
                      }`}>
                        {selectedService === service.id && (
                          <Check className="w-4 h-4 text-white" />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Select Date & Time */}
        {currentStep === 2 && (
          <div data-testid="step-datetime">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground uppercase tracking-tight mb-6">
              Select Date & Time
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <Label className="text-lg font-semibold mb-4 block">Choose a Date</Label>
                <Card className="p-4">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => {
                      setSelectedDate(date);
                      setSelectedTime('');
                    }}
                    disabled={disabledDays}
                    defaultMonth={new Date()}
                    fromDate={new Date()}
                    className="rounded-md"
                    data-testid="booking-calendar"
                  />
                </Card>
                <p className="text-sm text-muted-foreground mt-2">
                  Mon-Fri: 12 PM, 5 PM | Sat-Sun: 9 AM, 2 PM
                </p>
              </div>

              <div>
                <Label className="text-lg font-semibold mb-4 block">Choose a Time</Label>
                {selectedDate ? (
                  loading ? (
                    <div className="flex items-center justify-center h-40">
                      <div className="spinner"></div>
                    </div>
                  ) : availableSlots.length > 0 ? (
                    <div className="grid grid-cols-2 gap-3">
                      {availableSlots.map((slot) => (
                        <Button
                          key={slot}
                          variant={selectedTime === slot ? "default" : "outline"}
                          className={`h-14 ${selectedTime === slot ? 'bg-sky-500 hover:bg-sky-600' : ''}`}
                          onClick={() => setSelectedTime(slot)}
                          data-testid={`time-slot-${slot}`}
                        >
                          {formatTime(slot)}
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <Card className="p-6 text-center">
                      <p className="text-muted-foreground">No available slots for this date</p>
                    </Card>
                  )
                ) : (
                  <Card className="p-6 text-center">
                    <CalendarIcon className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground">Please select a date first</p>
                  </Card>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Vehicle Details */}
        {currentStep === 3 && (
          <div data-testid="step-vehicle">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground uppercase tracking-tight mb-6">
              Vehicle Information
            </h2>
            <Card className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="make">Make *</Label>
                  <Input
                    id="make"
                    placeholder="e.g., Toyota"
                    value={vehicle.make}
                    onChange={(e) => setVehicle({...vehicle, make: e.target.value})}
                    className="mt-2 h-12"
                    data-testid="vehicle-make"
                  />
                </div>
                <div>
                  <Label htmlFor="model">Model *</Label>
                  <Input
                    id="model"
                    placeholder="e.g., Camry"
                    value={vehicle.model}
                    onChange={(e) => setVehicle({...vehicle, model: e.target.value})}
                    className="mt-2 h-12"
                    data-testid="vehicle-model"
                  />
                </div>
                <div>
                  <Label htmlFor="license">License Plate *</Label>
                  <Input
                    id="license"
                    placeholder="e.g., ABC 123"
                    value={vehicle.license_plate}
                    onChange={(e) => setVehicle({...vehicle, license_plate: e.target.value})}
                    className="mt-2 h-12"
                    data-testid="vehicle-license"
                  />
                </div>
                <div>
                  <Label htmlFor="color">Color (Optional)</Label>
                  <Input
                    id="color"
                    placeholder="e.g., Silver"
                    value={vehicle.color}
                    onChange={(e) => setVehicle({...vehicle, color: e.target.value})}
                    className="mt-2 h-12"
                    data-testid="vehicle-color"
                  />
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Step 4: Client Details */}
        {currentStep === 4 && (
          <div data-testid="step-client">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground uppercase tracking-tight mb-6">
              Your Information
            </h2>
            <Card className="p-6 mb-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    placeholder="John Doe"
                    value={client.name}
                    onChange={(e) => setClient({...client, name: e.target.value})}
                    className="mt-2 h-12"
                    data-testid="client-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email * (required for invoice)</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    value={client.email}
                    onChange={(e) => setClient({...client, email: e.target.value})}
                    className="mt-2 h-12"
                    data-testid="client-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    placeholder="(905) 555-1234"
                    value={client.phone}
                    onChange={(e) => setClient({...client, phone: e.target.value})}
                    className="mt-2 h-12"
                    data-testid="client-phone"
                  />
                </div>
              </div>
            </Card>

            {/* Location & Accessibility */}
            <Card className="p-6 mb-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-sky-500" />
                Service Location
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="has_driveway"
                    checked={location.has_driveway}
                    onCheckedChange={(checked) => {
                      setLocation({...location, has_driveway: checked});
                      if (!checked && !location.has_water_access) {
                        setLocation(prev => ({...prev, has_driveway: checked, drop_off_location: true}));
                      }
                    }}
                    data-testid="has-driveway"
                  />
                  <div>
                    <Label htmlFor="has_driveway" className="cursor-pointer">
                      I have an accessible driveway
                    </Label>
                    <p className="text-sm text-muted-foreground">Check if we can access your driveway for service</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="has_water"
                    checked={location.has_water_access}
                    onCheckedChange={(checked) => {
                      setLocation({...location, has_water_access: checked});
                      if (!checked && !location.has_driveway) {
                        setLocation(prev => ({...prev, has_water_access: checked, drop_off_location: true}));
                      }
                    }}
                    data-testid="has-water"
                  />
                  <div>
                    <Label htmlFor="has_water" className="cursor-pointer">
                      I have a water faucet and hose available
                    </Label>
                    <p className="text-sm text-muted-foreground">Check if you have outdoor water access</p>
                  </div>
                </div>

                {location.has_water_access === false && location.has_driveway && (
                  <div className="flex items-start gap-3 ml-6 p-3 bg-sky-500/10 rounded-lg">
                    <Checkbox
                      id="bring_hose"
                      checked={location.bring_hose}
                      onCheckedChange={(checked) => setLocation({...location, bring_hose: checked})}
                      data-testid="bring-hose"
                    />
                    <div>
                      <Label htmlFor="bring_hose" className="cursor-pointer text-sky-600">
                        <Droplets className="w-4 h-4 inline mr-1" />
                        Please bring a hose (we can bring one if needed)
                      </Label>
                    </div>
                  </div>
                )}

                {(!location.has_driveway || !location.has_water_access) && (
                  <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="drop_off"
                        checked={location.drop_off_location}
                        onCheckedChange={(checked) => setLocation({...location, drop_off_location: checked})}
                        data-testid="drop-off"
                      />
                      <div>
                        <Label htmlFor="drop_off" className="cursor-pointer font-semibold text-amber-700">
                          Drop off vehicle at our location
                        </Label>
                        <p className="text-sm text-amber-600 mt-1">
                          <MapPin className="w-4 h-4 inline mr-1" />
                          {DROP_OFF_ADDRESS}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          If you don&apos;t have driveway or water access, you can drop off your vehicle at our location.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {!location.drop_off_location && (
                  <div className="mt-4">
                    <Label htmlFor="address">Service Address *</Label>
                    <Input
                      id="address"
                      placeholder="123 Main St, City, Province"
                      value={client.address}
                      onChange={(e) => setClient({...client, address: e.target.value})}
                      className="mt-2 h-12"
                      data-testid="client-address"
                    />
                  </div>
                )}

                {location.drop_off_location && (
                  <div className="p-3 bg-green-500/10 rounded-lg">
                    <p className="text-sm text-green-700">
                      <Check className="w-4 h-4 inline mr-1" />
                      Your vehicle will be serviced at: <strong>{DROP_OFF_ADDRESS}</strong>
                    </p>
                  </div>
                )}
              </div>
            </Card>

            {/* Special Requests & Referral */}
            <Card className="p-6">
              <div className="space-y-6">
                <div>
                  <Label htmlFor="requests">Special Requests (Optional)</Label>
                  <Textarea
                    id="requests"
                    placeholder="Any special instructions or areas to focus on..."
                    value={specialRequests}
                    onChange={(e) => setSpecialRequests(e.target.value)}
                    className="mt-2 min-h-24"
                    data-testid="special-requests"
                  />
                  <p className="text-sm text-muted-foreground mt-2">
                    Note: We dispose of all garbage during our service.
                  </p>
                </div>

                {/* Discount Code */}
                <div className="p-4 bg-green-500/5 border border-green-500/20 rounded-lg">
                  <Label htmlFor="discount" className="flex items-center gap-2 text-green-600 font-semibold">
                    <Gift className="w-5 h-5" />
                    Have a Discount Code?
                  </Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      id="discount"
                      placeholder="Enter promo code"
                      value={discountCode}
                      onChange={(e) => {
                        setDiscountCode(e.target.value);
                        setDiscountInfo(null);
                      }}
                      className="h-12 uppercase"
                      data-testid="discount-code"
                    />
                    <Button 
                      type="button"
                      onClick={validateDiscountCode}
                      disabled={!discountCode.trim() || validatingDiscount}
                      className="bg-green-600 hover:bg-green-700 h-12 px-6"
                      data-testid="apply-discount-btn"
                    >
                      {validatingDiscount ? "..." : "Apply"}
                    </Button>
                  </div>
                  {discountInfo?.valid && (
                    <div className="mt-3 p-3 bg-green-500/10 rounded-lg">
                      <p className="text-green-700 font-medium">
                        <Check className="w-4 h-4 inline mr-1" />
                        {discountInfo.description} — {discountInfo.percent}% OFF!
                      </p>
                      <p className="text-sm text-green-600 mt-1">
                        You save: ${discountInfo.discount_amount.toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>

                <div className="p-4 bg-sky-500/5 border border-sky-500/20 rounded-lg">
                  <Label htmlFor="referral" className="flex items-center gap-2 text-sky-600">
                    <Gift className="w-5 h-5" />
                    Referral Code (Get $5 off!)
                  </Label>
                  <Input
                    id="referral"
                    type="email"
                    placeholder="Enter the email of who referred you"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value)}
                    className="mt-2 h-12"
                    data-testid="referral-code"
                    disabled={discountInfo?.valid}
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    {discountInfo?.valid 
                      ? "Promo code already applied. Only one discount per booking."
                      : "If someone referred you, enter their email to receive $5 off your first booking!"}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Step 5: Confirmation */}
        {currentStep === 5 && (
          <div data-testid="step-confirm">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground uppercase tracking-tight mb-6">
              Confirm Your Booking
            </h2>
            <Card className="p-6 mb-6">
              <div className="space-y-6">
                <div className="pb-4 border-b border-border">
                  <h3 className="font-semibold text-muted-foreground text-sm uppercase mb-2">Service</h3>
                  <p className="text-xl font-bold text-foreground">{getSelectedServiceData()?.name}</p>
                  {discountInfo?.valid ? (
                    <div className="mt-1">
                      <p className="text-lg text-muted-foreground line-through">${getSelectedServiceData()?.price}</p>
                      <p className="text-2xl font-bold text-green-500">${discountInfo.final_price.toFixed(2)}</p>
                      <p className="text-sm text-green-600">{discountInfo.percent}% off applied!</p>
                    </div>
                  ) : (
                    <p className="text-2xl font-bold text-sky-500 mt-1">${getSelectedServiceData()?.price}</p>
                  )}
                </div>

                <div className="pb-4 border-b border-border">
                  <h3 className="font-semibold text-muted-foreground text-sm uppercase mb-2">Date & Time</h3>
                  <p className="text-lg text-foreground">
                    {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
                  </p>
                  <p className="text-lg text-foreground">{formatTime(selectedTime)}</p>
                </div>

                <div className="pb-4 border-b border-border">
                  <h3 className="font-semibold text-muted-foreground text-sm uppercase mb-2">Vehicle</h3>
                  <p className="text-lg text-foreground">
                    {vehicle.make} {vehicle.model} {vehicle.color && `(${vehicle.color})`}
                  </p>
                  <p className="text-muted-foreground">License: {vehicle.license_plate}</p>
                </div>

                <div className="pb-4 border-b border-border">
                  <h3 className="font-semibold text-muted-foreground text-sm uppercase mb-2">Service Location</h3>
                  {location.drop_off_location ? (
                    <p className="text-lg text-foreground">
                      <MapPin className="w-4 h-4 inline mr-1 text-sky-500" />
                      Drop-off at: {DROP_OFF_ADDRESS}
                    </p>
                  ) : (
                    <>
                      <p className="text-lg text-foreground">{client.address}</p>
                      {location.bring_hose && (
                        <p className="text-sm text-sky-600 mt-1">
                          <Droplets className="w-4 h-4 inline mr-1" />
                          We&apos;ll bring a hose
                        </p>
                      )}
                    </>
                  )}
                </div>

                <div className="pb-4 border-b border-border">
                  <h3 className="font-semibold text-muted-foreground text-sm uppercase mb-2">Contact</h3>
                  <p className="text-lg text-foreground">{client.name}</p>
                  <p className="text-muted-foreground">{client.email}</p>
                  <p className="text-muted-foreground">{client.phone}</p>
                </div>

                {discountInfo?.valid && (
                  <div className="pb-4 border-b border-border">
                    <h3 className="font-semibold text-muted-foreground text-sm uppercase mb-2 flex items-center gap-2">
                      <Gift className="w-4 h-4 text-green-500" />
                      Discount Applied
                    </h3>
                    <p className="text-green-600 font-medium">{discountInfo.description}</p>
                    <p className="text-green-600">{discountInfo.percent}% OFF — You save ${discountInfo.discount_amount.toFixed(2)}!</p>
                  </div>
                )}

                {referralCode && !discountInfo?.valid && (
                  <div className="pb-4 border-b border-border">
                    <h3 className="font-semibold text-muted-foreground text-sm uppercase mb-2 flex items-center gap-2">
                      <Gift className="w-4 h-4 text-sky-500" />
                      Referral Applied
                    </h3>
                    <p className="text-foreground">Referred by: {referralCode}</p>
                    <p className="text-sm text-green-600 mt-1">$5 discount will be applied if referral is valid</p>
                  </div>
                )}

                {specialRequests && (
                  <div>
                    <h3 className="font-semibold text-muted-foreground text-sm uppercase mb-2">Special Requests</h3>
                    <p className="text-foreground">{specialRequests}</p>
                  </div>
                )}
              </div>
            </Card>

            <Card className="p-6 bg-sky-500/5 border-sky-500/20">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-sky-500/10 rounded-lg">
                  <Sparkles className="w-6 h-6 text-sky-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Payment via E-Transfer or Cash</h3>
                  <p className="text-muted-foreground text-sm">
                    After confirming, you&apos;ll receive an email invoice with payment instructions. 
                    Please send your e-transfer or pay cash to complete the booking.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
            className="px-6"
            data-testid="btn-back"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>

          {currentStep < 5 ? (
            <Button
              onClick={handleNext}
              className="bg-sky-500 hover:bg-sky-600 px-6"
              data-testid="btn-next"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-sky-500 hover:bg-sky-600 px-8"
              data-testid="btn-confirm"
            >
              {submitting ? (
                <>
                  <div className="spinner mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  Confirm Booking
                  <Check className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
